<template>
    <DModal
        :title="title"
        :show="showModal"
        @close="closeModal"
    >
        <DCard padding>
            <template v-if="errorMessage !== null">
                <DAlert
                    class="m-0"
                    color="danger"
                >
                    <span class="websocket-loading-error">{{ errorMessage }}</span>
                </DAlert>
                <p
                    v-if="internalSupportLink"
                    class="mt-3"
                >
                    {{ __('messages.websocket_loading.error_ticket') }}
                    <a
                        :href="internalSupportLink"
                        target="_blank"
                    >
                        {{ internalSupportLink }}
                    </a>
                </p>
            </template>
            <template v-else>
                <div class="d-flex justify-content-center">
                    <DFieldLoading
                        :is-big="true"
                        :is-loading="true"
                    />
                </div>
                <p
                    v-if="statusHint !== null"
                    class="mt-3 text-center"
                >
                    {{ statusHint }}
                </p>
                <p
                    v-if="dismissNote !== null"
                    class="mt-3 text-center text-medium-emphasis"
                >
                    {{ dismissNote }}
                </p>
                <template
                    v-if="timeoutReached"
                >
                    <p class="mt-3">
                        {{ __('messages.websocket_loading.soft_timeout_hint') }}
                    </p>
                    <p
                        v-if="internalSupportLink"
                        class="mt-3"
                    >
                        {{ __('messages.websocket_loading.soft_timeout_ticket') }}
                        <a
                            :href="internalSupportLink"
                            target="_blank"
                        >
                            {{ internalSupportLink }}
                        </a>
                    </p>
                    <p
                        v-if="showQueueMonitoringHint"
                    >
                        {{ __('messages.websocket_loading.soft_timeout_further_information') }}
                        <a
                            :href="route('administrations.queue.index')"
                            target="_blank"
                        >
                            {{ __('messages.sitemap.administration.queue_jobs.queue_jobs') }}
                        </a>
                    </p>
                </template>
            </template>
        </DCard>
    </DModal>
</template>

<script setup>
import { route } from '~@/Composables/route'
import { __ } from '~@/Composables/translation'
import DFieldLoading from '~@/Components/Common/DFieldLoading'
import { computed, ref, onMounted, onUnmounted } from 'vue'
import { usePage } from '@inertiajs/vue3'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    softTimeout: {
        type: Number,
        default: null
    },
    title: {
        type: String,
        default: null
    },
    internalSupportLink: {
        type: String,
        default: () => { return '' }
    },
    // When set, the job has reached a terminal failure and this message replaces the spinner.
    errorMessage: {
        type: String,
        default: null
    },
    // Optional line below the spinner telling the user what the job is currently doing.
    statusHint: {
        type: String,
        default: null
    },
    // Optional line explaining what closing this modal does — and, for the long-running jobs, what it does not do.
    // Worth stating explicitly: the close button dismisses a view of the job, it does not stop the job, and for a
    // destructive operation the difference between those two readings matters.
    dismissNote: {
        type: String,
        default: null
    }
})

// 'close', not 'closeModal': all but one consumer bound @close, so the old name meant the close button emitted an event
// nobody listened for and the modal could not be dismissed by hand at all. Consumers pass showModal, not show — the
// latter only ever reached DModal by attribute fallthrough, which is what hid the mismatched event name here.
const emit = defineEmits(['close'])

// The soft timeout is a hint only. It tells the user the job is taking longer than expected and never reports a
// failure: only the backend knows whether the job actually failed, and it says so through errorMessage.
const timeoutReached = ref(false)
const timeout = ref(null)

const clearTimers = () => {
    clearTimeout(timeout.value)
}

onMounted(() => {
    if (props.softTimeout !== null) {
        timeout.value = setTimeout(
            () => {
                timeoutReached.value = true
            },
            props.softTimeout * 1000
        )
    }
})

// The modal is usually rendered behind a v-if, so it can disappear without closeModal being called.
onUnmounted(clearTimers)

const showQueueMonitoringHint = computed(() => usePage().props.auth.permissions.includes('queue.monitoring'))

const closeModal = () => {
    clearTimers()
    emit('close')
}

</script>

<style scoped>
.websocket-loading-error {
    white-space: pre-line;
}
</style>
